/**
 * The consent gate.
 *
 * Nothing dispatches to GA until `analytics_storage` is granted. That is a legal
 * requirement, but a naive implementation of it also silently destroys data: a
 * visitor who starts a quiz, answers three questions, and THEN clicks "Accept all"
 * has their first three events dropped, so every funnel looks like it has a
 * catastrophic step-1 abandon rate. So events fired before consent are QUEUED and
 * flushed once, in order, the moment consent arrives.
 *
 * ## Why we don't import @wtc/cookie-consent
 *
 * The banner owns consent; this package owns events. Importing the banner here would
 * make every app that tracks an event also pull in vanilla-cookieconsent and its CSS.
 * Instead the state is read from the two things the banner already produces — the
 * `cc_cookie` consent record and the Consent Mode dataLayer entry — and updated via
 * a DOM event the banner dispatches. One-way dependency, no bundle coupling.
 */

/** DOM event the consent banner dispatches when the analytics category changes. */
export const CONSENT_CHANGE_EVENT = "wtc:analytics-consent-change";

/** vanilla-cookieconsent's persisted consent record. */
const CONSENT_COOKIE_NAME = "cc_cookie";

/** The consent category that gates analytics, shared with @wtc/cookie-consent. */
export const ANALYTICS_CONSENT_CATEGORY = "analytics";

export type ConsentState = "granted" | "denied";

export interface ConsentChangeDetail {
  readonly analytics: ConsentState;
}

/**
 * Read the CURRENT analytics-consent state from the banner's own cookie.
 *
 * Returns "denied" when the cookie is absent (nobody has chosen yet) — deny-by-
 * default is the only safe reading of "no answer".
 */
export function readConsentFromCookie(cookieString?: string): ConsentState {
  const source =
    cookieString ?? (typeof document === "undefined" ? "" : document.cookie);
  const match = new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE_NAME}=([^;]*)`).exec(source);
  if (!match?.[1]) return "denied";

  try {
    const decoded = decodeURIComponent(match[1]);
    const parsed: unknown = JSON.parse(decoded);
    if (!parsed || typeof parsed !== "object") return "denied";
    const categories = (parsed as { categories?: unknown }).categories;
    if (!Array.isArray(categories)) return "denied";
    return categories.includes(ANALYTICS_CONSENT_CATEGORY) ? "granted" : "denied";
  } catch {
    return "denied";
  }
}

let overrideState: ConsentState | null = null;

/**
 * Current consent state: an explicit override (set by the banner's change event, or
 * by a test) wins over the cookie, which is the durable record.
 */
export function getConsentState(): ConsentState {
  return overrideState ?? readConsentFromCookie();
}

export function isAnalyticsGranted(): boolean {
  return getConsentState() === "granted";
}

type Listener = (state: ConsentState) => void;
const listeners = new Set<Listener>();

/**
 * Record a consent decision and notify listeners.
 *
 * Idempotent by design: granting twice must not flush the queue twice, which is why
 * listeners only run on an actual state CHANGE.
 */
export function setConsentState(state: ConsentState): void {
  const previous = getConsentState();
  overrideState = state;
  if (previous === state) return;
  for (const listener of listeners) listener(state);
}

/** Subscribe to consent changes. Returns an unsubscribe function. */
export function onConsentChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Bridge the banner's DOM event into this module. Call once from the provider.
 * Returns a teardown function.
 */
export function attachConsentListener(): () => void {
  if (typeof window === "undefined") return () => undefined;

  const handler = (event: Event): void => {
    const detail = (event as CustomEvent<ConsentChangeDetail>).detail;
    if (detail?.analytics === "granted" || detail?.analytics === "denied") {
      setConsentState(detail.analytics);
    } else {
      // No usable detail — re-read the durable record rather than guessing.
      setConsentState(readConsentFromCookie());
    }
  };

  window.addEventListener(CONSENT_CHANGE_EVENT, handler);
  return () => window.removeEventListener(CONSENT_CHANGE_EVENT, handler);
}

/**
 * Announce a consent decision to every listener in the page (the analytics layer
 * included). Called by the consent banner, not by feature code.
 */
export function publishConsentChange(state: ConsentState): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<ConsentChangeDetail>(CONSENT_CHANGE_EVENT, {
      detail: { analytics: state },
    })
  );
}

/** Test seam: forget the override so the cookie is authoritative again. */
export function resetConsentOverride(): void {
  overrideState = null;
}
